/**
 * quickScoreUtils.js
 *
 * Pure helpers for the "Quick Score" feature — a no-login, non-persisted
 * scoring session. Builds a synthetic "match" object shaped exactly like the
 * real match objects used by the existing scoreboard components
 * (components/match/scoreboards/*), so those components can be reused as-is.
 */
import { getSportDefaults, getSportConfigKey } from "../config/sportsConfig";

// Sports whose dedicated scoreboard component manages its own multi-set
// progression (see VolleyballScoreboard / BadmintonScoreboard /
// TableTennisScoreboard). Only these sports get a "Best of" selector in the
// Quick Score setup form — other sports (Basketball, Football) use a single
// continuous score. Keys are sportsConfig keys.
const SPORTS_WITH_SETS = ["volleyball", "badminton", "tabletennis"];

const DEFAULT_SETS_BY_SPORT = {
  volleyball: 5,
  badminton: 3,
  tabletennis: 5,
};

// Table tennis is also played best of 7 (ITTF)
const SET_OPTIONS_BY_SPORT = {
  tabletennis: [1, 3, 5, 7],
};

const SPORT_ICONS = {
  football: "⚽",
  soccer: "⚽",
  basketball: "🏀",
  volleyball: "🏐",
  badminton: "🏸",
  "table tennis": "🏓",
  tennis: "🎾",
};

export const getSportIcon = (sportName) =>
  SPORT_ICONS[(sportName || "").toLowerCase()] || "🏆";

export const sportSupportsSets = (sportName) =>
  SPORTS_WITH_SETS.includes(getSportConfigKey(sportName));

export const getDefaultSets = (sportName) =>
  DEFAULT_SETS_BY_SPORT[getSportConfigKey(sportName)] ?? 1;

export const SET_OPTIONS = [1, 3, 5];

/** "Best of" choices for a sport's Quick Score setup. */
export const getSetOptions = (sportName) =>
  SET_OPTIONS_BY_SPORT[getSportConfigKey(sportName)] || SET_OPTIONS;

/**
 * Build a synthetic, in-memory "match" object for the Quick Score scoring
 * screen. Nothing here is persisted or sent to the backend — it only needs
 * to satisfy the shape the existing scoreboard components expect.
 */
export const buildQuickMatch = ({ sportName, team1Name, team2Name, sets }) => {
  const defaults = getSportDefaults(sportName);
  const totalGames = sportSupportsSets(sportName) ? sets || getDefaultSets(sportName) : 1;

  return {
    id: `quickscore_${Date.now()}`,
    sport_name: sportName,
    status: "in_progress",
    participant_1_name: team1Name,
    participant_2_name: team2Name,
    participant1Type: "team",
    participant2Type: "team",
    matchConfig: {
      game_rules: {
        total_games: totalGames,
        points_to_win: defaults.points_to_win,
        win_by_margin: defaults.win_by_margin,
        max_points: defaults.max_points,
      },
      scoring_rules: {
        points_to_win: defaults.points_to_win,
        win_by_margin: defaults.win_by_margin,
        max_points: defaults.max_points,
        // Basketball: FIBA periods (ignored by other sports)
        ...(defaults.periods ? {
          periods: defaults.periods,
          period_minutes: defaults.period_minutes,
          overtime_minutes: defaults.overtime_minutes,
        } : {}),
      },
    },
  };
};

/** Validate the setup form; returns an error message string, or null if valid. */
export const validateQuickScoreSetup = ({ sportName, team1Name, team2Name }) => {
  if (!sportName) return "Please select a sport.";
  if (!team1Name || !team1Name.trim()) return "Please enter a name for Team / Player 1.";
  if (!team2Name || !team2Name.trim()) return "Please enter a name for Team / Player 2.";
  if (team1Name.trim().toLowerCase() === team2Name.trim().toLowerCase()) {
    return "Team / Player names must be different.";
  }
  return null;
};

/**
 * Resolve the final summary (winner / draw / scores) for the "Match Complete"
 * screen from whatever data is available: a sport-specific scoreboard's
 * completionData (volleyball/badminton), or the raw current score for
 * sports that only expose a manual "Complete" button.
 */
export const resolveQuickScoreSummary = ({ completionData, currentScore, team1Name, team2Name }) => {
  if (completionData) {
    const score1 = completionData.participant1Score ?? currentScore?.participant1 ?? 0;
    const score2 = completionData.participant2Score ?? currentScore?.participant2 ?? 0;
    return {
      winnerName: completionData.winnerName || null,
      isDraw: !completionData.winnerName,
      score1,
      score2,
      sets: completionData.gameScores || null,
    };
  }

  const score1 = currentScore?.participant1 || 0;
  const score2 = currentScore?.participant2 || 0;
  const winnerName = score1 > score2 ? team1Name : score2 > score1 ? team2Name : null;

  return {
    winnerName,
    isDraw: score1 === score2,
    score1,
    score2,
    sets: null,
  };
};
